<template>
  <BbPopup v-model="confirmPopupOpened" :showCloseIcon="true" contentClass="layouts-list-popup">
    <template #popup-header>
      <TextBody tag="span" i18n-key="author.pickLayout" :body-type="3" weight="medium" />
    </template>
    <BbTreeview
      v-for="group in groupedAvailableLayouts"
      :key="group.name"
      :title="group.name"
      hide-icon
      initial-state
      class="treeview-group"
    >
      <BbTreeviewItem v-for="layout in group.layouts" :key="layout.id">
        <BbDashboardCard class="amp-card" fill-width>
          <template #image>
            <div v-if="layout.thumbnail" class="thumbnail-wrapper" @click="onLayoutClick(layout)">
              <v-img :src="layout.thumbnail" width="100%" max-height="168" min-height="118" contain />
            </div>
            <div v-else class="missing-thumbnail-wrapper" @click="onLayoutClick(layout)">
              <BbIcon class="icon" name="medialibrary" />
            </div>
          </template>
          <div class="bb-dashboard-card__title_container" data-app>
            <div class="bb-dashboard-card__title hover-active">{{ layout.name }}</div>
          </div>
        </BbDashboardCard>
      </BbTreeviewItem>
    </BbTreeview>
  </BbPopup>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { groupBy } from 'lodash';

import TextBody from '~/components/common/TextBody';
import useDocumentEditor from '~/composables/useDocumentEditor';
import useModals from '~/composables/useModals';
import useConversations from '@/composables/useConversations';

export default {
  components: { TextBody },
  setup(props, { emit }) {
    const { getLayoutsByParentId, currentTemplate } = useDocumentEditor();
    const { currentConversation } = useConversations();
    const { layoutsListOpened, setLayoutsListOpened } = useModals();

    const availableLayouts = ref([]);

    const confirmPopupOpened = computed({
      get: () => layoutsListOpened.value,
      set: (val) => setLayoutsListOpened(val),
    });
    const groupedAvailableLayouts = computed(() => {
      const grouped = groupBy(availableLayouts.value, (layout) => layout.templateName.split('/')[0]);

      return Object.keys(grouped)
        .filter((group) => grouped[group].length > 0)
        .map((group) => ({
          name: group,
          layouts: grouped[group].map((layout) => {
            return {
              id: layout.id,
              name: layout.templateName.split('/')[1],
              thumbnail: layout.url || '',
            };
          }),
        }));
    });

    const onLayoutClick = (layout) => {
      emit('layout-selected', layout);
      setLayoutsListOpened(false);
    };
    watch(layoutsListOpened, async (newVal) => {
      if (newVal && !availableLayouts.value.length) {
        const parentId = currentTemplate.value?.parentId || currentConversation.value?.designSystemId;
        availableLayouts.value = await getLayoutsByParentId(parentId, true);
      }
    });

    return {
      emit,
      confirmPopupOpened,
      groupedAvailableLayouts,
      onLayoutClick,
    };
  },
};
</script>

<style scoped lang="scss">
.amp-card {
  width: 215px !important;
  height: 128px !important;

  :deep(.bb-dashboard-card__content) {
    padding: $bb-spacing-2;
  }

  :deep(.bb-dashboard-card__title) {
    font-size: 11px;
    color: $bb-neutral-2;
  }

  .missing-thumbnail-wrapper {
    width: 100% !important;
    background: $bb-interface-4 !important;

    .icon {
      font-size: 50px;
      color: $bb-interface-7;
    }
  }
}

.treeview-group {
  width: 100% !important;

  :deep(.treeview-item) {
    display: flex;
    flex-wrap: wrap;
    column-gap: $bb-spacing-2;
    row-gap: $bb-spacing-2;
  }

  :deep(.bb-treeview-item) {
    padding: 0 !important;
  }

  :deep(.bb-treeview__button) {
    margin: $bb-spacing-4 0;
  }
}
</style>
